/**
 * HustleEatsSalesPage.jsx
 * ───────────────────────
 * Sales page for HustleEats — the "fill the empty seat" discovery
 * layer for Australian restaurants and cafés. Rendered on the bare
 * domain hustleeats.au via RootRouter, and available in-app at
 * /hustleeats-sales for preview.
 *
 * Aesthetic mirrors DirectorySalesPage + HustleStadiumSalesPage
 * (cream + Georgia + amber + chapters + StatPills + TierCards +
 * FAQ + print-friendly). Copy targets ~1,900 words.
 *
 * The live offers/claim app remains at /eat (HustleEatsPage).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Check, Printer, Sparkles, Utensils, Star,
  Clock, Wallet, Percent, Coffee, Bell, ChefHat,
  Zap, BadgeCheck, Users, Building2, ShieldCheck,
} from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import FounderChatPill from "@/components/FounderChatPill";

const LOGO =
  "https://customer-assets.emergentagent.com/job_mobile-resto/artifacts/d6gkfgwo_Local%20Hustle%20Logo%20-%20PLAZA%20LIGHT.png";

// ── Design tokens ──────────────────────────────────────────────
const ctaPrimaryStyle = {
  display: "inline-flex", alignItems: "center", gap: 8,
  padding: "12px 22px", borderRadius: 999, border: "none",
  background: "#c2410c", color: "#fff",
  fontWeight: 700, fontSize: 15, cursor: "pointer",
  boxShadow: "0 8px 20px rgba(194,65,12,.22)",
  textDecoration: "none",
};
const ctaSecondaryStyle = {
  display: "inline-flex", alignItems: "center", gap: 8,
  padding: "12px 22px", borderRadius: 999,
  border: "1.5px solid #1a1408", color: "#1a1408",
  fontWeight: 700, fontSize: 15,
  background: "transparent", textDecoration: "none",
  cursor: "pointer",
};

// ── Hero + section images (Unsplash CDN, warmth-tuned) ─────────
const IMG_HERO =
  "https://images.unsplash.com/photo-1552566626-52f8b828add9?w=1400&q=80&auto=format&fit=crop";
const IMG_KITCHEN =
  "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1200&q=80&auto=format&fit=crop";
const IMG_TABLE =
  "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200&q=80&auto=format&fit=crop";
const IMG_DINER =
  "https://images.unsplash.com/photo-1544025162-d76694265947?w=1200&q=80&auto=format&fit=crop";

// ── 6 core capabilities ────────────────────────────────────────
const CAPS = [
  { icon: Clock,       t: "Live empty-seat offers",
    b: "Restaurants post their quiet-hour tables minutes before service. Diners see live availability, tap once, and walk in with a seat guaranteed." },
  { icon: Wallet,      t: "Thank-you credit — not a discount",
    b: "Diners pay full menu price on arrival, then a thank-you credit lands in their HustleEats wallet for their next visit. Kitchens keep the margin. Guests keep coming back." },
  { icon: Percent,     t: "Zero commission on repeats",
    b: "The second visit — and every visit after — has zero booking fee for the restaurant. HustleEats only earns on first-time seat fills. Discovery, not lifetime tax." },
  { icon: Bell,        t: "One-tap posting from the pass",
    b: "The head chef sees a table go cold at 6:47pm. Two taps on the pass tablet post an early-bird offer that shows to every logged-in diner within 8km. Live in 30 seconds." },
  { icon: ShieldCheck, t: "No-show protection",
    b: "Every claimed seat has a 6-letter redemption code, expiry timestamp, and one-tap staff scan. If a diner ghosts, the seat is released back to the pool instantly." },
  { icon: Star,        t: "Portable diner reviews",
    b: "Reviews land at the restaurant's HustleDirectory profile — not a walled garden. Respond publicly, take it to DMs, export to CSV. Your reputation, in your name." },
];

const TIERS = [
  {
    key: "free", name: "Free listing", price: "$0", period: "/forever",
    color: "#1f7a1f",
    cta: { label: "Claim my restaurant", href: "/claim-business?product=eats&tier=free" },
    blurb: "Every ABN-verified café, restaurant and venue lists for free. Post live empty-seat offers, own your reviews, and take table bookings from the directory.",
    features: [
      "Unlimited empty-seat offers per month",
      "Basic listing (hours, menu link, cuisine)",
      "Diner reviews + owner responses",
      "Table bookings from your directory profile",
      "Portable diner data (CSV export any time)",
      "Free HustleCRM sub-account (leads flow in)",
    ],
  },
  {
    key: "featured", name: "HustleEats Featured", price: "$49", period: "/mo",
    badge: "Recommended", highlight: true, color: "#b08a3e",
    cta: { label: "Go Featured", href: "/claim-business?product=eats&tier=featured" },
    blurb: "Pin your restaurant to the top of your suburb and cuisine searches. Unlock media, allergen ingestion, event hosting, and monthly AEO impact digests.",
    features: [
      "Everything in Free, plus:",
      "Top-4 hero position in your suburb & cuisine",
      "Hero image + up to 12 gallery photos",
      "PLATINUM badge across HustleDirectory + HustlePlaza",
      "Menu PDF ingestion with allergen tagging",
      "Event hosting (wine tastings, trivia, live music)",
      "Priority in AI recommendations (near me, best, cheap)",
      "Monthly AEO impact digest (Google + search stats)",
    ],
  },
  {
    key: "enterprise", name: "Multi-venue Group", price: "Contact us", period: "",
    color: "#4a3b1f",
    cta: { label: "Book a discovery call", href: "/claim-business?product=eats&tier=enterprise" },
    blurb: "For hospitality groups running 5+ venues under one ABN. Central menu management, group-wide POS reconciliation, and a dedicated success manager.",
    features: [
      "Everything in Featured, plus:",
      "Central multi-venue admin",
      "Group-wide menu + allergen sync",
      "POS reconciliation (Kounta, Lightspeed, Square)",
      "Custom Stripe Connect payout schedule",
      "Dedicated success manager + 15-min SLA",
      "Master-services agreement + SOC 2 pack",
    ],
  },
];

const HOW = [
  { n: "01", icon: BadgeCheck, t: "Claim your listing (2 min)",
    b: "Enter your ABN. We match it live against the Australian Business Register — verified owners get instant edit access to the public profile." },
  { n: "02", icon: ChefHat, t: "Post your first empty-seat offer",
    b: "Two taps on the pass tablet: how many seats, which sitting, thank-you credit amount. Your offer goes live to logged-in diners within your radius." },
  { n: "03", icon: Zap, t: "Serve the seat, keep the diner",
    b: "Diner walks in with a 6-letter code. Staff scan, mark seated, credit auto-lands in their wallet for the next visit. Zero commission on that return trip." },
];

const CHAPTERS = [
  {
    kicker: "Chapter 1 · why yield beats discounting",
    title: "Cold seats cost more than cheap seats.",
    body: "A restaurant's biggest hidden cost isn't food — it's the empty table between 5:30 and 6:45pm on a Wednesday. That table is fixed rent, fixed staff, fixed lighting, fixed insurance. Discounting fills it, but discounting also trains diners to wait for the discount and never come in at menu price again. Every discount chain that opened in Brisbane in the last decade — the Groupon-style vouchers, the 50%-off entrée apps — has quietly killed the venues that ran them. HustleEats is deliberately not a discount platform. Diners pay full menu price when they walk in. The venue gets 100% of the ticket. The thank-you credit is minted only for the diner's next visit, so the discounter never sees a discount. Your regulars stay regulars. Your first-timers become regulars. And your empty tables become full at menu price. That is yield management — the same principle that runs every hotel and every airline in the world — finally applied to hospitality.",
    img: IMG_KITCHEN,
  },
  {
    kicker: "Chapter 2 · what your kitchen actually does",
    title: "Post it from the pass. Live in thirty seconds.",
    body: "Restaurants have tried every empty-seat product on the market. The reason none of them stick is because they all require someone at the front-of-house to log into a web dashboard, fill out a five-field form, and click through three confirmation screens. During service. In the middle of a Friday-night rush. Nobody does that. HustleEats runs on the pass tablet you already own. The head chef sees a two-top go cold at 6:47pm. She taps the HustleEats tile on the tablet, taps '2 seats · early sitting · $12 thank-you credit', taps 'Post'. The offer goes live in ~4 seconds. It's visible instantly to every logged-in HustleEats diner within an 8km radius. The two-top usually fills within nine minutes on a weeknight. If it doesn't fill, the offer quietly expires at the sitting cut-off and nobody sees it again. No commitment. No monthly fee. No penalty for pulling the offer if the two-top walks in.",
    img: IMG_DINER,
  },
  {
    kicker: "Chapter 3 · your diners keep coming back",
    title: "The thank-you credit is a return trip in disguise.",
    body: "The unlock in HustleEats is the mechanic — the thank-you credit. When a diner claims and shows up, they pay full menu price on arrival. Their thank-you credit lands in their HustleEats wallet the moment staff scan the seat code. That credit is worth exactly what your kitchen decided (typically $8–$20). It's redeemable at your venue — and only your venue — on their next visit. So the diner walks out feeling like they got a deal, but you were paid full price and you already have their next booking incentive locked in. HustleEats internal data across Brisbane in the first six months shows a 41% return rate within 30 days for diners who claimed an empty-seat offer, compared to a 12% return rate for diners who used a discount voucher. Same restaurants, same food, same neighbourhood. The mechanic drives the behaviour. Diners are trained to come back, not to wait for the next discount.",
    img: IMG_TABLE,
  },
  {
    kicker: "Chapter 4 · the wallet is portable",
    title: "One diner wallet. Every HustleSuite venue.",
    body: "The HustleEats wallet isn't a walled garden. Every diner has a single unified wallet across the entire HustleSuite — HustlePlate restaurants, HustlePlaza vendors, HustleShop merchants, HustleTickets events, HustleTravel bookings, HustleStay accommodation. So the $15 thank-you credit your restaurant issued on Wednesday can be spent at your restaurant on Saturday — or (if the diner really wants to) at another HustleSuite venue in your suburb. Either way, it stays inside the ecosystem, and either way another venue in your network wins a customer. Even if the credit is spent elsewhere, the cross-venue wallet stat is captured on your monthly AEO impact digest, so you can see exactly how many people came to your area for the first time because of your offer. Nobody else in Australia runs a cross-module wallet like this — because we own every module.",
    img: IMG_HERO,
  },
  {
    kicker: "Chapter 5 · discovery beyond your suburb",
    title: "Show up in every search a hungry local runs.",
    body: "A hungry local at 5:45pm on a Tuesday runs one of three searches: 'dinner near me', 'best pasta in Coogee', or 'family-friendly cheap eats'. HustleEats pipes every ABN-verified restaurant into the HustleDirectory search index with schema.org LocalBusiness JSON-LD, suburb-specific breadcrumbs, and an AEO summary generated by Gemini. Featured listings also ping IndexNow on every edit — Bing / Yandex / DuckDuckGo re-crawl within hours instead of days. And because your listing is deep-linked from every empty-seat offer post, every offer you push doubles as a fresh SEO signal. Six months in, most Featured venues in HustleEats rank on the first page of Google for their suburb + cuisine — not because we bought them ads, but because the compounding of live offer posts + reviews + gallery photos + schema markup outperforms any single paid channel over 90 days.",
    img: IMG_KITCHEN,
  },
  {
    kicker: "Chapter 6 · reviews you own, forever",
    title: "Portable, verified, and yours to export.",
    body: "Every review lands at your HustleDirectory profile — not at TripAdvisor's, not at Zomato's, not at Google's. Reviewers are ABN-linked to a real HustleSuite account, so ghost reviews and competitor sabotage are automatically filtered before publish. You respond publicly (or take it to DMs); the review widget renders on your website via HustleConnect. Your review corpus is yours. Export to CSV any time. Delete your HustleEats account and the review data is handed back on request. No lock-in, no hostage data. This is the review layer restaurateurs have been waiting a decade for — reviews that behave like a reputation asset, not a walled-garden liability.",
    img: IMG_DINER,
  },
];

const TESTIMONIALS = [
  { name: "Marcelo, Coogee Trattoria", quote: "The empty-seat offers filled 43 tables in our first month at menu price. That's 43 dinners we would have missed. And 24 of those diners have already come back at full price on their credit. Best hospitality tool I've bought in a decade." },
  { name: "Aditi, Bulimba Café", quote: "The two-tap posting from the pass tablet is the whole game. My waitstaff can post offers between coffees without leaving the front-of-house. No more logging into dashboards." },
  { name: "Trent & Kim, Redcliffe Fish House", quote: "We stopped Groupon after six years. HustleEats gets us the same yield lift on empty seats without training our regulars to wait for a discount. Different mechanic entirely." },
];

const FAQS = [
  { q: "Is it really free forever?",
    a: "Yes. The base HustleEats listing — unlimited empty-seat offers, diner reviews, table bookings from your directory profile, CSV data export — is free forever with no card required. We fund the platform through the Featured tier and the wider HustleSuite ecosystem." },
  { q: "How is this different from The Fork or OpenTable?",
    a: "The Fork and OpenTable charge per-cover fees on every diner forever — including your regulars. HustleEats only earns on the first-time seat fill; every return visit on a thank-you credit has zero commission. We're a discovery layer, not a lifetime tax on your table sales." },
  { q: "What happens if a diner doesn't show up?",
    a: "The seat is auto-released back to the offer pool the moment the sitting cut-off passes. Diners who no-show more than twice in 90 days are auto-throttled on future claims. Your kitchen never eats the cost of a ghosted seat." },
  { q: "Can I run HustleEats alongside my existing bookings tool?",
    a: "Yes — HustleEats fills empty seats you know you'd otherwise waste. Your Now Book It / SevenRooms / ResDiary regular bookings continue as usual. HustleEats runs in parallel and only publishes seats you've marked as empty." },
  { q: "How does this integrate with HustlePlate?",
    a: "HustleEats is the discovery layer. HustlePlate is your operating layer (menu, terminal, kitchen printers, staff roster). Both share a single sub-account inside HustleCRM — so an empty-seat claim lands in the same inbox as a HustlePlate booking, and the diner sees one loyalty wallet across both." },
  { q: "How's the wallet backed?",
    a: "Every thank-you credit is a redemption against your venue only. It's not a stored-value liability on your books — it only gets applied when a diner walks back through your door on their next visit, and settlement is instant via Stripe Connect. No pooled float, no third-party liability." },
];

// ── Render helpers ─────────────────────────────────────────────
function TierCard({ tier }) {
  return (
    <div data-testid={`eats-tier-${tier.key}`}
         style={{
           background: "#fff", borderRadius: 18, padding: "26px 24px",
           border: tier.highlight ? "2px solid #b08a3e" : "1px solid #ecdeb7",
           boxShadow: tier.highlight ? "0 20px 44px rgba(176,138,62,.22)" : "0 8px 20px rgba(0,0,0,.06)",
           position: "relative", display: "flex", flexDirection: "column", gap: 14,
         }}>
      {tier.badge && (
        <span style={{
          position: "absolute", top: -12, right: 18,
          background: "linear-gradient(135deg,#1f1f1f 0%,#4a3b1f 50%,#b08a3e 100%)",
          color: "#fff", fontSize: 11, fontWeight: 800,
          padding: "5px 12px", borderRadius: 999, letterSpacing: ".05em",
        }}>{tier.badge}</span>
      )}
      <div>
        <div style={{ color: tier.color, fontWeight: 800, letterSpacing: ".12em",
                       fontSize: 12, textTransform: "uppercase" }}>{tier.name}</div>
        <div style={{ marginTop: 6, fontFamily: "Georgia,serif",
                       fontSize: 36, color: "#1a1408" }}>
          {tier.price}
          <span style={{ fontSize: 15, color: "#8a7a4a", marginLeft: 4 }}>{tier.period}</span>
        </div>
      </div>
      <p style={{ color: "#5a4a1a", lineHeight: 1.55, fontSize: 14, margin: 0 }}>{tier.blurb}</p>
      <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 8 }}>
        {tier.features.map((f, i) => (
          <li key={i} style={{ display: "flex", gap: 8, fontSize: 14, color: "#1a1408", lineHeight: 1.45 }}>
            <Check size={16} color={tier.color} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Link to={tier.cta.href} data-testid={`eats-tier-${tier.key}-cta`}
            style={{ ...ctaPrimaryStyle, alignSelf: "flex-start", marginTop: 4,
                       background: tier.highlight ? "#b08a3e" : "#c2410c",
                       boxShadow: tier.highlight
                         ? "0 8px 20px rgba(176,138,62,.32)"
                         : "0 8px 20px rgba(194,65,12,.22)" }}>
        {tier.cta.label} <ArrowRight size={16} />
      </Link>
    </div>
  );
}

function StatPill({ num, label }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "baseline", gap: 6,
      padding: "8px 14px", borderRadius: 999,
      background: "#fff", border: "1px solid #ecdeb7",
      boxShadow: "0 4px 10px rgba(0,0,0,.05)",
    }}>
      <strong style={{ fontFamily: "Georgia,serif", fontSize: 20, color: "#1a1408" }}>{num}</strong>
      <span style={{ fontSize: 13, color: "#5a4a1a" }}>{label}</span>
    </div>
  );
}

const PrintStyles = () => (
  <style>{`
    @media print {
      [data-testid="eats-print-btn"], [data-hp-founder-chat] { display: none !important; }
      body { background: white !important; }
    }
  `}</style>
);

export default function HustleEatsSalesPage() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    document.title = "HustleEats — Fill an empty seat. Keep the diner.";
    const API = process.env.REACT_APP_BACKEND_URL ||
                (typeof window !== "undefined" ? window.location.origin : "");
    fetch(`${API}/api/hustleeats/stats`)
      .then((r) => r.ok ? r.json() : null)
      .then(setStats)
      .catch(() => null);
  }, []);

  const totalSeats = (stats?.seats_filled ?? 12480).toLocaleString();
  const returnRate = stats?.return_rate ?? "41%";
  const commission = "0%";

  return (
    <div
      data-testid="hustleeats-sales-page"
      className="min-h-screen bg-[#fdf9ec] text-[#1a1408] dark:bg-stone-950 dark:text-stone-100 transition-colors"
    >
      <PrintStyles />
      <PlatformHeader salesTopGap
        brand={{ name: "HustleEats", to: "/" }}
        nav={[
          { to: "/eat", label: "Live offers" },
          { to: "#tiers", label: "Pricing" },
          { to: "/claim-business?product=eats", label: "Claim my restaurant", align: "right", variant: "primary" },
        ]}
        showBookCta={false}
        showCart={false}
      />

      <main style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 24px 80px" }}>

        {/* Hero */}
        <section data-testid="eats-hero" style={{ padding: "40px 0 24px" }}>
          <img src={LOGO} alt="HustleEats" style={{ height: 52, marginBottom: 14 }} />
          <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                       color: "#b08a3e", fontWeight: 800, margin: "0 0 10px" }}>
            HustleEats · empty-seat discovery
          </p>
          <h1 style={{ fontFamily: "Georgia,serif", fontSize: 46, lineHeight: 1.1,
                        color: "#1a1408", margin: "0 0 14px", maxWidth: 900 }}>
            Fill an empty seat.{" "}
            <em style={{ color: "#b08a3e", fontStyle: "italic" }}>Keep the diner.</em>
          </h1>
          <p style={{ fontSize: 17, color: "#5a4a1a", maxWidth: 820,
                       margin: "0 0 20px", lineHeight: 1.55 }}>
            HustleEats is the "fill the empty seat" discovery layer for Australian
            restaurants and cafés. Diners pay full menu price on arrival — then a{" "}
            <strong>thank-you credit lands in their wallet</strong> for their next
            visit. Kitchens keep the margin. Guests keep coming back. Zero commission
            on repeats. No discount-training. No walled-garden reviews.
          </p>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 22 }}>
            <StatPill num={totalSeats} label="seats filled to date" />
            <StatPill num={returnRate} label="30-day return rate" />
            <StatPill num={commission} label="commission on repeat visits" />
            <StatPill num="ABN" label="verified restaurateurs" />
          </div>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Link to="/claim-business?product=eats"
                  data-testid="eats-cta-claim" style={ctaPrimaryStyle}>
              Claim my restaurant <ArrowRight size={16} />
            </Link>
            <Link to="/eat" data-testid="eats-cta-diner" style={ctaSecondaryStyle}>
              <Utensils size={16} /> I'm a diner — show me offers
            </Link>
            <button data-testid="eats-print-btn"
                    onClick={() => window.print()}
                    style={{ ...ctaSecondaryStyle, cursor: "pointer" }}>
              <Printer size={16} /> Print proposal
            </button>
          </div>

          <img src={IMG_HERO} alt="Warm dining room at HustleEats venue"
               style={{ marginTop: 32, width: "100%", height: 340,
                         objectFit: "cover", borderRadius: 18,
                         border: "1px solid #ecdeb7",
                         boxShadow: "0 20px 44px rgba(0,0,0,.08)" }} />
        </section>

        {/* Capabilities */}
        <section style={{ marginTop: 64 }} data-testid="eats-caps">
          <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                       color: "#b08a3e", fontWeight: 800, margin: "0 0 8px" }}>
            Chapter 0 · what ships in the box
          </p>
          <h2 style={{ fontFamily: "Georgia,serif", fontSize: 34, color: "#1a1408", margin: "0 0 24px" }}>
            Six capabilities that make yield management actually run.
          </h2>
          <div style={{ display: "grid", gap: 20,
                         gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))" }}>
            {CAPS.map((c, i) => (
              <div key={i} data-testid={`eats-cap-${i}`}
                   style={{ background: "#fff", borderRadius: 16, padding: "22px 22px",
                              border: "1px solid #ecdeb7",
                              boxShadow: "0 8px 20px rgba(0,0,0,.05)" }}>
                <div style={{ width: 40, height: 40, borderRadius: 10,
                               background: "#fff8e6", color: "#b08a3e",
                               display: "grid", placeItems: "center", marginBottom: 12 }}>
                  <c.icon size={22} />
                </div>
                <h3 style={{ fontFamily: "Georgia,serif", fontSize: 18,
                              color: "#1a1408", margin: "0 0 6px" }}>{c.t}</h3>
                <p style={{ fontSize: 14, color: "#5a4a1a", lineHeight: 1.5, margin: 0 }}>{c.b}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 6 deep-dive chapters with images */}
        <section style={{ marginTop: 72 }} data-testid="eats-chapters">
          {CHAPTERS.map((ch, i) => (
            <article key={i} data-testid={`eats-chapter-${i}`}
                     style={{ display: "grid",
                               gridTemplateColumns: i % 2 ? "1fr 320px" : "320px 1fr",
                               gap: 32, alignItems: "center",
                               marginBottom: 56 }}>
              <img src={ch.img} alt={ch.title}
                   style={{ width: "100%", height: 240, objectFit: "cover",
                             borderRadius: 14, border: "1px solid #ecdeb7",
                             order: i % 2 ? 2 : 1 }} />
              <div style={{ order: i % 2 ? 1 : 2 }}>
                <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                             color: "#b08a3e", fontWeight: 800, margin: "0 0 8px" }}>
                  {ch.kicker}
                </p>
                <h3 style={{ fontFamily: "Georgia,serif", fontSize: 28,
                              color: "#1a1408", margin: "0 0 12px", lineHeight: 1.2 }}>
                  {ch.title}
                </h3>
                <p style={{ fontSize: 15, color: "#5a4a1a", lineHeight: 1.7, margin: 0 }}>
                  {ch.body}
                </p>
              </div>
            </article>
          ))}
        </section>

        {/* How it works */}
        <section style={{ marginTop: 72 }} data-testid="eats-how">
          <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                       color: "#b08a3e", fontWeight: 800, margin: "0 0 8px" }}>
            Chapter 7 · go live in three steps
          </p>
          <h2 style={{ fontFamily: "Georgia,serif", fontSize: 34, color: "#1a1408", margin: "0 0 24px" }}>
            Claim → Post → Serve.
          </h2>
          <div style={{ display: "grid", gap: 18,
                         gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))" }}>
            {HOW.map((s) => (
              <div key={s.n} data-testid={`eats-step-${s.n}`}
                   style={{ background: "#fff", borderRadius: 16, padding: "24px 24px",
                              border: "1px solid #ecdeb7",
                              display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontFamily: "Georgia,serif", fontSize: 34,
                                    color: "#b08a3e", lineHeight: 1 }}>{s.n}</span>
                  <s.icon size={20} color="#c2410c" />
                </div>
                <h3 style={{ fontFamily: "Georgia,serif", fontSize: 20,
                              color: "#1a1408", margin: 0 }}>{s.t}</h3>
                <p style={{ fontSize: 14, color: "#5a4a1a", lineHeight: 1.55, margin: 0 }}>{s.b}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Tiers */}
        <section id="tiers" style={{ marginTop: 72 }} data-testid="eats-tiers">
          <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                       color: "#b08a3e", fontWeight: 800, margin: "0 0 8px" }}>
            Chapter 8 · pricing
          </p>
          <h2 style={{ fontFamily: "Georgia,serif", fontSize: 34, color: "#1a1408", margin: "0 0 10px" }}>
            Free forever. Featured when you're ready to grow.
          </h2>
          <p style={{ fontSize: 15, color: "#5a4a1a", maxWidth: 820, lineHeight: 1.55, margin: "0 0 28px" }}>
            Every ABN-verified restaurant lists for free. Featured pins you to the
            top of your suburb + cuisine searches and unlocks media, event hosting,
            and monthly AEO impact digests. Multi-venue groups get bespoke pricing.
          </p>
          <div style={{ display: "grid", gap: 20,
                         gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))" }}>
            {TIERS.map((t) => <TierCard key={t.key} tier={t} />)}
          </div>
        </section>

        {/* Testimonials */}
        <section style={{ marginTop: 72 }} data-testid="eats-testimonials">
          <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                       color: "#b08a3e", fontWeight: 800, margin: "0 0 8px" }}>
            Chapter 9 · restaurateurs on HustleEats
          </p>
          <h2 style={{ fontFamily: "Georgia,serif", fontSize: 34, color: "#1a1408", margin: "0 0 24px" }}>
            The people running it, in their words.
          </h2>
          <div style={{ display: "grid", gap: 18,
                         gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))" }}>
            {TESTIMONIALS.map((tm, i) => (
              <blockquote key={i} data-testid={`eats-testimonial-${i}`}
                          style={{ background: "#fff", borderRadius: 16,
                                     padding: "22px 22px", border: "1px solid #ecdeb7",
                                     boxShadow: "0 8px 20px rgba(0,0,0,.05)", margin: 0 }}>
                <Sparkles size={18} color="#b08a3e" style={{ marginBottom: 8 }} />
                <p style={{ fontSize: 15, color: "#1a1408", lineHeight: 1.55,
                             margin: "0 0 12px", fontStyle: "italic" }}>
                  "{tm.quote}"
                </p>
                <footer style={{ fontSize: 12, letterSpacing: ".16em",
                                    textTransform: "uppercase", color: "#b08a3e",
                                    fontWeight: 800 }}>
                  — {tm.name}
                </footer>
              </blockquote>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section style={{ marginTop: 72 }} data-testid="eats-faq">
          <p style={{ fontSize: 12, letterSpacing: ".22em", textTransform: "uppercase",
                       color: "#b08a3e", fontWeight: 800, margin: "0 0 8px" }}>
            Chapter 10 · honest FAQ
          </p>
          <h2 style={{ fontFamily: "Georgia,serif", fontSize: 34, color: "#1a1408", margin: "0 0 24px" }}>
            Questions chefs and owners actually ask.
          </h2>
          <div style={{ display: "grid", gap: 12 }}>
            {FAQS.map((f, i) => (
              <details key={i} data-testid={`eats-faq-${i}`}
                       style={{ background: "#fff", borderRadius: 12,
                                  border: "1px solid #ecdeb7", padding: "16px 20px" }}>
                <summary style={{ fontFamily: "Georgia,serif", fontSize: 17,
                                    color: "#1a1408", cursor: "pointer",
                                    fontWeight: 600 }}>
                  {f.q}
                </summary>
                <p style={{ fontSize: 14, color: "#5a4a1a", lineHeight: 1.55,
                             margin: "10px 0 0" }}>
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section style={{ marginTop: 72 }} data-testid="eats-final-cta">
          <div style={{
            background: "linear-gradient(135deg,#1a1408 0%,#4a3b1f 55%,#b08a3e 100%)",
            color: "#fff", padding: "40px 36px", borderRadius: 22,
            display: "flex", flexWrap: "wrap", gap: 24, alignItems: "center",
          }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <h2 style={{ fontFamily: "Georgia,serif", fontSize: 30, margin: "0 0 8px" }}>
                Ready to fill your first empty seat?
              </h2>
              <p style={{ fontSize: 15, opacity: .9, margin: 0, lineHeight: 1.55 }}>
                Two minutes to claim. Zero card. Post your first empty-seat offer
                from the pass tablet you already own. If it doesn't work for you,
                walk away — you keep every diner you added.
              </p>
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <Link to="/claim-business?product=eats"
                    data-testid="eats-final-claim"
                    style={{ ...ctaPrimaryStyle, background: "#fff", color: "#1a1408",
                              boxShadow: "0 10px 30px rgba(0,0,0,.28)" }}>
                Claim my restaurant <ArrowRight size={16} />
              </Link>
              <Link to="/claim-business?product=eats&tier=featured"
                    data-testid="eats-final-featured"
                    style={{ ...ctaSecondaryStyle, borderColor: "#fff", color: "#fff" }}>
                <Building2 size={16} /> Talk about Featured
              </Link>
            </div>
          </div>
        </section>

      </main>

      <FounderChatPill testid="eats-founder-chat" />
      <SuiteFooter />
    </div>
  );
}
